import { DEPLOYMENT_STATUS_LABELS, VERSION_STATE_LABELS, type DeploymentStatus, type VersionState } from '@lia/contracts';
import { Rocket01Icon, WorkHistoryIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatDateTime } from '../../lib/format.js';
import { StatusBadge } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { EmptyState, Skeleton } from '../primitives/states.js';
import { DEPLOYMENT_STATUS_TONE, VERSION_STATE_TONE } from './status.js';

export interface PromptDeployment {
  /** e.g. "Aircall · Línea Torre Alameda", "WhatsApp (Kapso)" */
  target: string;
  status: DeploymentStatus;
  /** Provider message or required action (no secrets). */
  detail?: string;
  updatedAt?: string;
}

export interface PromptVersion {
  id: string;
  version: number;
  state: VersionState;
  author: string;
  createdAt: string;
  note?: string;
  deployments?: readonly PromptDeployment[];
}

export interface PromptVersionPanelProps {
  versions: readonly PromptVersion[];
  timezone: string;
  /** Publish a tested version. Hidden when undefined (no permission). */
  onPublish?: (id: string) => void;
  /** Roll back to an archived (previously published) version. */
  onRollback?: (id: string) => void;
  /** Id of the version currently being published/rolled back. */
  pendingId?: string | null;
  loading?: boolean;
  className?: string;
}

/** Version history with honest deployment status per target (saved ≠ applied). */
export const PromptVersionPanel = React.forwardRef<HTMLDivElement, PromptVersionPanelProps>(function PromptVersionPanel(
  { versions, timezone, onPublish, onRollback, pendingId, loading, className },
  ref,
) {
  if (loading) {
    return (
      <div ref={ref} role="status" aria-label="Cargando versiones" className={cn('grid gap-3', className)}>
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }
  if (versions.length === 0) {
    return (
      <div ref={ref} className={className}>
        <EmptyState compact icon={<Icon icon={WorkHistoryIcon} aria-hidden />} title="Sin versiones" description="Guarda un borrador para empezar el historial." />
      </div>
    );
  }
  return (
    <div ref={ref} className={cn('grid', className)}>
      <ol aria-label="Versiones de instrucciones" className="grid divide-y divide-border-subtle rounded-card border border-border-subtle bg-surface-default">
        {versions.map((v) => {
          const deployments = v.deployments ?? [];
          const notApplied = v.state === 'published' && deployments.some((d) => d.status !== 'applied');
          return (
            <li key={v.id} className="grid gap-2 px-4 py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="grid gap-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="ef-tabular text-body font-semibold text-text-primary">Versión {v.version}</span>
                    <StatusBadge tone={VERSION_STATE_TONE[v.state]}>{VERSION_STATE_LABELS[v.state]}</StatusBadge>
                    {notApplied ? <StatusBadge tone="warning">Aún no aplicada en todos los destinos</StatusBadge> : null}
                  </div>
                  <p className="text-meta text-text-secondary">
                    {v.author} · <span className="ef-tabular">{formatDateTime(v.createdAt, timezone)}</span>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {onPublish && v.state === 'tested' ? (
                    <Button size="sm" leadingIcon={<Icon icon={Rocket01Icon} aria-hidden />} loading={pendingId === v.id} onClick={() => onPublish(v.id)}>
                      Publicar
                    </Button>
                  ) : null}
                  {onRollback && v.state === 'archived' ? (
                    <Button size="sm" variant="secondary" leadingIcon={<Icon icon={WorkHistoryIcon} aria-hidden />} loading={pendingId === v.id} onClick={() => onRollback(v.id)}>
                      Restaurar
                    </Button>
                  ) : null}
                </div>
              </div>
              {v.note ? <p className="text-body text-text-primary">{v.note}</p> : null}
              {deployments.length > 0 ? (
                <ul aria-label={`Aplicación de la versión ${v.version}`} className="grid gap-1.5 rounded-control bg-surface-subtle p-2.5">
                  {deployments.map((d) => (
                    <li key={d.target} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body">
                      <span className="min-w-0 flex-1 text-text-primary">{d.target}</span>
                      <StatusBadge tone={DEPLOYMENT_STATUS_TONE[d.status]}>{DEPLOYMENT_STATUS_LABELS[d.status]}</StatusBadge>
                      {d.detail ? <span className="basis-full text-meta text-text-secondary">{d.detail}</span> : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
});
